// Base URL — change this to your deployed backend URL
const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// ── TOKEN HELPERS ─────────────────────────────────────────────────────────
export const getToken = () => localStorage.getItem("uni_token");
export const setToken = (t) => localStorage.setItem("uni_token", t);
export const removeToken = () => localStorage.removeItem("uni_token");

// ── BASE FETCH ────────────────────────────────────────────────────────────
async function api(endpoint, options = {}) {
  const token = getToken();
  const config = {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
    ...options,
  };
  if (config.body && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }
  const res = await fetch(`${BASE_URL}${endpoint}`, config);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "API Error");
  return data;
}

// ── AUTH ──────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password, role) => api("/auth/login", { method: "POST", body: { email, password, role } }),
  me: () => api("/auth/me"),
  changePassword: (currentPassword, newPassword) => api("/auth/change-password", { method: "POST", body: { currentPassword, newPassword } }),
};

// ── USERS ─────────────────────────────────────────────────────────────────
export const usersAPI = {
  getStudents: () => api("/users/students"),
  createStudent: (formData) => api("/users/students", { method: "POST", body: formData }),
  updateStudent: (id, formData) => api(`/users/students/${id}`, { method: "PUT", body: formData }),
  deleteStudent: (id) => api(`/users/students/${id}`, { method: "DELETE" }),
  setAdminAccess: (id, password) => api(`/users/students/${id}/admin-access`, { method: "PUT", body: { password } }),
  updateFees: (id, data) => api(`/users/fees/${id}`, { method: "PUT", body: data }),
  getNotifications: () => api("/users/notifications"),
  markAllRead: () => api("/users/notifications/read-all", { method: "PUT" }),
};

// ── ACADEMIC ──────────────────────────────────────────────────────────────
export const academicAPI = {
  // Degrees
  getDegrees: () => api("/academic/degrees"),
  createDegree: (data) => api("/academic/degrees", { method: "POST", body: data }),
  updateDegree: (id, data) => api(`/academic/degrees/${id}`, { method: "PUT", body: data }),
  deleteDegree: (id) => api(`/academic/degrees/${id}`, { method: "DELETE" }),
  // Programs
  getPrograms: () => api("/academic/programs"),
  createProgram: (data) => api("/academic/programs", { method: "POST", body: data }),
  updateProgram: (id, data) => api(`/academic/programs/${id}`, { method: "PUT", body: data }),
  deleteProgram: (id) => api(`/academic/programs/${id}`, { method: "DELETE" }),
  // Courses
  getCourses: (programId) => api(`/academic/courses${programId ? `?programId=${programId}` : ""}`),
  createCourse: (data) => api("/academic/courses", { method: "POST", body: data }),
  updateCourse: (id, data) => api(`/academic/courses/${id}`, { method: "PUT", body: data }),
  deleteCourse: (id) => api(`/academic/courses/${id}`, { method: "DELETE" }),
  // Modules
  getModules: (courseId) => api(`/academic/modules${courseId ? `?courseId=${courseId}` : ""}`),
  createModule: (data) => api("/academic/modules", { method: "POST", body: data }),
  updateModule: (id, data) => api(`/academic/modules/${id}`, { method: "PUT", body: data }),
  deleteModule: (id) => api(`/academic/modules/${id}`, { method: "DELETE" }),
  // Lectures
  getLectures: (moduleId) => api(`/academic/lectures${moduleId ? `?moduleId=${moduleId}` : ""}`),
  createLecture: (formData) => api("/academic/lectures", { method: "POST", body: formData }),
  updateLecture: (id, formData) => api(`/academic/lectures/${id}`, { method: "PUT", body: formData }),
  deleteLecture: (id) => api(`/academic/lectures/${id}`, { method: "DELETE" }),
};

// ── ASSESSMENT ────────────────────────────────────────────────────────────
export const assessmentAPI = {
  // Assignments
  getAssignments: (courseId) => api(`/assessment/assignments${courseId ? `?courseId=${courseId}` : ""}`),
  createAssignment: (formData) => api("/assessment/assignments", { method: "POST", body: formData }),
  updateAssignment: (id, formData) => api(`/assessment/assignments/${id}`, { method: "PUT", body: formData }),
  deleteAssignment: (id) => api(`/assessment/assignments/${id}`, { method: "DELETE" }),
  // Submissions
  getSubmissions: (assignmentId) => api(`/assessment/assignments/${assignmentId}/submissions`),
  mySubmissions: () => api("/assessment/submissions/my"),
  submitAssignment: (assignmentId, formData) => api(`/assessment/assignments/${assignmentId}/submit`, { method: "POST", body: formData }),
  gradeSubmission: (id, data) => api(`/assessment/submissions/${id}/grade`, { method: "PUT", body: data }),
  autoSubmit: () => api("/assessment/submissions/auto-submit", { method: "POST" }),
  // Exams
  getExams: (courseId) => api(`/assessment/exams${courseId ? `?courseId=${courseId}` : ""}`),
  createExam: (data) => api("/assessment/exams", { method: "POST", body: data }),
  updateExam: (id, data) => api(`/assessment/exams/${id}`, { method: "PUT", body: data }),
  deleteExam: (id) => api(`/assessment/exams/${id}`, { method: "DELETE" }),
  // Results
  getResults: (examId) => api(`/assessment/exams/${examId}/results`),
  myResults: () => api("/assessment/results/my"),
  submitExam: (examId, answers) => api(`/assessment/exams/${examId}/submit`, { method: "POST", body: { answers } }),
};

// ── COMMUNICATION ─────────────────────────────────────────────────────────
export const commAPI = {
  getAnnouncements: () => api("/communication/announcements"),
  createAnnouncement: (data) => api("/communication/announcements", { method: "POST", body: data }),
  updateAnnouncement: (id, data) => api(`/communication/announcements/${id}`, { method: "PUT", body: data }),
  deleteAnnouncement: (id) => api(`/communication/announcements/${id}`, { method: "DELETE" }),
  getQA: (courseId) => api(`/communication/qa${courseId ? `?courseId=${courseId}` : ""}`),
  postQuestion: (data) => api("/communication/qa", { method: "POST", body: data }),
  answerQA: (id, answer) => api(`/communication/qa/${id}/answer`, { method: "PUT", body: { answer } }),
  aiAnswerQA: (id, answer) => api(`/communication/qa/${id}/ai-answer`, { method: "PUT", body: { answer } }),
  deleteQA: (id) => api(`/communication/qa/${id}`, { method: "DELETE" }),
  getSetting: (key) => api(`/communication/settings/${key}`),
  saveSetting: (key, value) => api(`/communication/settings/${key}`, { method: "PUT", body: { value } }),
  aiKeyExists: () => api("/communication/settings/ai-key/exists"),
  askAI: (id) => api(`/communication/qa/${id}/ask-ai`, { method: "POST" }),

};

// ── UNIVERSITIES ──────────────────────────────────────────────────────────
// Simple lookup only — name/slug/logo. No data isolation, courses/exams/etc.
// stay fully shared regardless of which university a student is tagged with.
export const universitiesAPI = {
  getAll: () => api("/universities"),
  getBySlug: (slug) => api(`/universities/by-slug/${slug}`),
  create: (formData) => api("/universities", { method: "POST", body: formData }),
  update: (id, formData) => api(`/universities/${id}`, { method: "PUT", body: formData }),
  remove: (id) => api(`/universities/${id}`, { method: "DELETE" }),
};
